import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { useSearchParams } from "react-router-dom";
import { api, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart3, CheckCircle2, Clock } from "lucide-react";
import { fmtDateTime } from "@/pages/AdminAccessLogs";

const CAT_LABEL = { ricarica: "Ricarica", sim: "SIM", esim: "eSIM", giftcard: "Gift Card", gift_card: "Gift Card" };
const STATUS_CLS = "bg-amber-500 hover:bg-amber-500 text-black";

export default function AdminOrdersReport() {
  const [data, setData] = useState({ concluded: { items: [], total: 0, count: 0 }, pending: { items: [], count: 0 } });
  const [query, setQuery] = useSearchParams();
  const days = ["7", "30", "90", "180"].includes(query.get("days")) ? query.get("days") : "30";
  const dealerId = query.get("dealer") || "all";
  const tab = ["pending", "credited"].includes(query.get("tab")) ? query.get("tab") : "conclusi";
  const changeFilter = (key, value) => { const next = new URLSearchParams(query); next.set(key, value); setQuery(next, { replace: true }); };
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/dealers").then((r) => setDealers(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { days };
    if (dealerId !== "all") params.dealer_id = dealerId;
    api.get("/admin/orders-report", { params }).then((r) => { if (active) setData(r.data); }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days, dealerId]);

  return (
    <div className="space-y-6" data-testid="admin-orders-report-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Analisi</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><BarChart3 className="w-7 h-7 text-primary" /> Report ordini & provvigioni</h1>
        <p className="text-sm text-muted-foreground mt-1">Ordini conclusi (SIM, gift card, ricariche) e pratiche da lavorare che generano provvigioni (Energia, Internet/ADSL, Italia Mobile).</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={dealerId} onValueChange={(value) => changeFilter("dealer", value)}>
          <SelectTrigger className="h-9 w-56" data-testid="report-dealer-filter"><SelectValue placeholder="Dealer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i dealer</SelectItem>
            {dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.company_name || d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={(value) => changeFilter("days", value)}>
          <SelectTrigger className="h-9 w-36" data-testid="report-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="7">7 giorni</SelectItem><SelectItem value="30">30 giorni</SelectItem><SelectItem value="90">90 giorni</SelectItem><SelectItem value="180">180 giorni</SelectItem></SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Badge variant="outline" className="gap-1" data-testid="report-concluded-total"><CheckCircle2 className="w-3 h-3 text-teal-500" /> Conclusi: {euro(data.concluded?.total || 0)}</Badge>
          <Badge variant="outline" className="gap-1" data-testid="report-pending-count"><Clock className="w-3 h-3 text-amber-500" /> Da lavorare: {data.pending?.count || 0}</Badge>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(value) => changeFilter("tab", value)}>
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="conclusi" data-testid="report-tab-conclusi"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Conclusi ({data.concluded?.count || 0})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="report-tab-pending"><Clock className="w-4 h-4 mr-1.5" /> Da lavorare ({data.pending?.count || 0})</TabsTrigger>
          <TabsTrigger value="credited" data-testid="report-tab-credited"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Provvigioni ripartite ({data.credited?.count || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="conclusi" className="mt-3">
          <Card className="glow-border">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Prodotto</TableHead><TableHead>Cliente</TableHead><TableHead>Dealer</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.concluded?.items || []).map((o) => (
                  <TableRow key={o.id} data-testid={`report-concluded-${o.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(o.created_at)}</TableCell>
                    <TableCell><Badge variant="outline">{CAT_LABEL[o.category] || o.category}</Badge></TableCell>
                    <TableCell className="text-sm"><EntityLink kind="order" id={o.id} data-testid={`report-order-link-${o.id}`}>{o.product_name}</EntityLink></TableCell>
                    <TableCell className="text-sm text-muted-foreground"><EntityLink kind="customer" id={o.customer_id} data-testid={`report-customer-link-${o.id}`}>{o.customer === "—" ? (o.customer_id ? "Scheda cliente" : "—") : o.customer}</EntityLink></TableCell>
                    <TableCell className="text-sm text-muted-foreground"><EntityLink id={o.dealer_id} data-testid={`report-order-dealer-link-${o.id}`}>{o.dealer_name}</EntityLink></TableCell>
                    <TableCell className="text-right font-medium">{euro(o.amount)}</TableCell>
                  </TableRow>
                ))}
                {(data.concluded?.items || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{loading ? "Caricamento…" : "Nessun ordine concluso nel periodo."}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-3">
          <div className="flex flex-wrap gap-2 mb-3" data-testid="report-pending-counts">
            {Object.entries((data.pending?.items || []).reduce((acc, p) => { acc[p.kind] = (acc[p.kind] || 0) + 1; return acc; }, {})).map(([kind, n]) => (
              <Badge key={kind} variant="outline" className="gap-1 py-1" data-testid={`report-count-${kind}`}><Clock className="w-3 h-3 text-amber-500" />{kind}: <span className="font-semibold">{n}</span></Badge>
            ))}
            {(data.pending?.items || []).length === 0 && <span className="text-sm text-muted-foreground">Nessuna pratica da lavorare.</span>}
          </div>
          <Card className="glow-border">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Servizio</TableHead><TableHead>Dettaglio</TableHead><TableHead>Cliente</TableHead><TableHead>Dealer</TableHead><TableHead className="text-right">Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.pending?.items || []).map((p) => (
                  <TableRow key={p.id} data-testid={`report-pending-${p.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(p.created_at)}</TableCell>
                    <TableCell><Badge className="bg-primary/15 text-primary hover:bg-primary/15">{p.kind}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground"><EntityLink kind={p.entity_kind} id={p.entity_kind ? p.id : null} data-testid={`report-practice-link-${p.id}`}>{p.detail || "Apri pratica"}</EntityLink></TableCell>
                    <TableCell className="text-sm"><EntityLink kind="customer" id={p.customer_id} data-testid={`report-practice-customer-link-${p.id}`}>{p.customer === "—" ? (p.customer_id ? "Scheda cliente" : "—") : p.customer}</EntityLink></TableCell>
                    <TableCell className="text-sm text-muted-foreground"><EntityLink id={p.dealer_id} data-testid={`report-practice-dealer-link-${p.id}`}>{p.dealer_name}</EntityLink></TableCell>
                    <TableCell className="text-right"><Badge className={STATUS_CLS}>{p.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {(data.pending?.items || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{loading ? "Caricamento…" : "Nessuna pratica da lavorare nel periodo."}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="credited" className="mt-3">
          <p className="text-sm text-muted-foreground mb-3" data-testid="report-credited-total">Totale rete (escluso admin): <strong className="text-foreground">{euro(data.credited?.total || 0)}</strong> lordi</p>
          <Card className="glow-border overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Servizio</TableHead><TableHead>Pratica</TableHead><TableHead>Cliente</TableHead><TableHead>Beneficiario</TableHead><TableHead>Ruolo</TableHead><TableHead className="text-right">Lordo</TableHead><TableHead className="text-right">Netto</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.credited?.items || []).map((c) => (
                  <TableRow key={c.id} data-testid={`report-credited-${c.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(c.created_at)}</TableCell>
                    <TableCell><Badge variant="outline">{c.service}</Badge></TableCell>
                    <TableCell className="text-sm font-mono">{c.ref || "—"}</TableCell>
                    <TableCell className="text-sm">{c.customer}</TableCell>
                    <TableCell className="text-sm">{c.beneficiary}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.role}</TableCell>
                    <TableCell className="text-right tabular-nums">{euro(c.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{euro(c.net)}</TableCell>
                    <TableCell><Badge className={STATUS_CLS}>{c.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {(data.credited?.items || []).length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">{loading ? "Caricamento…" : "Nessuna provvigione ripartita nel periodo."}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
